import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Typography,
  Row,
  Col,
  Card,
  Form,
  DatePicker,
  Input,
  Space,
  Button,
  Alert,
  Divider,
  Spin,
  message,
  Progress,
} from "antd";
import { CalendarOutlined, HomeOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import enGB from "antd/locale/en_GB";
import updateLocale from "dayjs/plugin/updateLocale";
import { useDispatch, useSelector } from "react-redux";
import { createRemoteRequest, editRemoteRequestCurrentUser, getMyRemoteRequests, getRemoteDaysBalance } from "../../slices/remoteRequest";
import "./_remoteRequest.scss";

// Configure dayjs to start week on Monday
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1, // 1 = Monday, 0 = Sunday
});

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const MAX_REMOTE_DAYS = 10;
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 300;
const CONSECUTIVE_DAYS_WARNING = 5;

const ApplyForRemoteRequestModal = ({
  title,
  id,
  open,
  handleClose,
  data,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const user = useSelector((state) => state.user.fetchMe);
  const { remoteDaysBalance } = useSelector((state) => state.remoteRequest || {});
  const myRemoteRequests = useSelector((state) => state.remoteRequest?.myRemoteRequests || []);

  useEffect(() => {
    if (open) {
      dispatch(getMyRemoteRequests());
      dispatch(getRemoteDaysBalance());
    }
  }, [open, dispatch]);

  // Check if editing
  const isEdit = useMemo(() => {
    return data && (data.isEdit || data._id);
  }, [data]);

  useEffect(() => {
    if (open && data && isEdit) {
      // Populate form with existing data for editing
      const startDate = data.startDate ? dayjs(data.startDate) : null;
      const endDate = data.endDate ? dayjs(data.endDate) : null;
      
      form.setFieldsValue({
        dateRange: startDate && endDate ? [startDate, endDate] : null,
        reason: (data.reason || "").trim(),
      });
      
      setDateRange(startDate && endDate ? [startDate, endDate] : null);
    } else if (!open) {
      form.resetFields();
      setDateRange(null);
      setFeedback(null);
    }
  }, [open, form, data, isEdit]);

  // Calculate remote work balance
  const totalRemoteDays = useMemo(() => MAX_REMOTE_DAYS, []);

  const usedRemoteDays = useMemo(() => {
    if (user?.remoteDays === undefined || user?.remoteDays === null) return 0;
    // remoteDays = 0 means used all 10 days (0 remaining)
    // remoteDays = 3 means 3 remaining (used 7 days)
    // remoteDays = -2 means 2 days over limit (used 12 days)
    if (user.remoteDays < 0) {
      // Negative means over limit: used days = 10 + absolute value
      return 10 + Math.abs(user.remoteDays);
    } else {
      // Positive or zero means: used days = 10 - remaining days
      return 10 - user.remoteDays;
    }
  }, [user?.remoteDays]);

  const remainingRemoteDays = useMemo(() => {
    if (user?.remoteDays === undefined || user?.remoteDays === null) return 10;
    return Math.max(0, user.remoteDays);
  }, [user?.remoteDays]);

  const isLimitReached = useMemo(() => {
    return user?.remoteDays !== undefined && user?.remoteDays < 0;
  }, [user?.remoteDays]);

  // Calculate number of days in selected range
  const calculateDays = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return end.diff(start, "day") + 1;
  }, []);

  const handleModalClose = useCallback(() => {
    if (submitting) return;
    form.resetFields();
    setDateRange(null);
    setFeedback(null);
    handleClose(id);
  }, [submitting, form, handleClose, id]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setFeedback(null);
    setDateRange(null);
  }, [form]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    form.setFieldsValue({ dateRange: dates });
  };

  const onFinish = async (values) => {
    if (!values.dateRange || !values.dateRange[0] || !values.dateRange[1]) {
      message.error("Please select a date range.");
      return;
    }

    const [startDate, endDate] = values.dateRange;
    const numberOfDays = calculateDays(startDate, endDate);
    const trimmedReason = (values.reason || "").trim();

    // Check if this request would result in negative balance (warning only)
    const newRemainingDays = remainingRemoteDays - numberOfDays;
    if (!isEdit && newRemainingDays < 0) {
      const excess = Math.abs(newRemainingDays);
      setFeedback({
        type: "warning",
        message: `This request will result in a negative balance of ${excess} day(s). The deficit will be deducted from your future remote work balance.`,
      });
    }

    try {
      setSubmitting(true);
      setFeedback(null);

      const submitData = {
        startDate: startDate.format("MM/DD/YYYY"),
        endDate: endDate.format("MM/DD/YYYY"),
        reason: trimmedReason,
        numberOfDays,
      };

      if (isEdit && data?._id) {
        await dispatch(
          editRemoteRequestCurrentUser({ data: submitData, id: data._id })
        ).unwrap();
        message.success("Remote work request updated successfully.");
      } else {
        await dispatch(createRemoteRequest(submitData)).unwrap();
        message.success("Remote work request submitted successfully.");
      }

      await dispatch(getMyRemoteRequests());
      form.resetFields();
      setDateRange(null);
      handleModalClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        (typeof error === "string" ? error : "Failed to submit remote work request. Please try again.");
      setFeedback({ type: "error", message: errorMessage });
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const modalBodyStyle = useMemo(
    () => ({
      maxHeight: "80vh",
      overflowY: "auto",
      overflowX: "hidden",
      padding: "0 24px 24px",
    }),
    []
  );

  const selectedDays = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return 0;
    return calculateDays(dateRange[0], dateRange[1]);
  }, [dateRange, calculateDays]);

  const headerTitle = isEdit
    ? "Edit Remote Work Request"
    : title || "Apply for Remote Work";

  const headerSubtitle = isEdit
    ? "Update your remote work request details and review your balance."
    : "Submit a new remote work request and keep track of your remote days balance.";

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      footer={null}
      destroyOnClose
      width={720}
      centered
      className="apply-for-leave-modal apply-for-remote-request-modal"
      bodyStyle={modalBodyStyle}
    >
      <div className="apply-for-leave-modal__header">
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          {headerTitle}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {headerSubtitle}
        </Typography.Paragraph>
      </div>

      {feedback && (
        <Alert
          type={feedback.type}
          message={feedback.message}
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setFeedback(null)}
        />
      )}

      <Divider style={{ margin: "24px 0" }} />

      {/* Remote Work Balance Card */}
      <Card bordered={false} className="apply-for-remote-request-modal__metric-card">
        <Row gutter={16}>
          <Col span={24}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong style={{ fontSize: 15, color: '#fff', fontWeight: 600 }}>
                Remote Work Balance
              </Typography.Text>
            </div>
            <Progress
              percent={Math.min((usedRemoteDays / totalRemoteDays) * 100, 100)}
              status={isLimitReached ? "exception" : "active"}
              strokeColor={isLimitReached ? "#ff4d4f" : "#ffffff"}
              trailColor="rgba(255, 255, 255, 0.3)"
              format={() => (
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                  {usedRemoteDays} day(s) / {totalRemoteDays} day(s)
                </span>
              )}
            />
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {isLimitReached ? (
                <Typography.Text style={{ color: '#fff', fontWeight: 500 }}>
                  ⚠️ Limit exceeded: {usedRemoteDays} day(s) used (over by {Math.abs(user?.remoteDays || 0)} day(s))
                </Typography.Text>
              ) : (
                <Typography.Text style={{ color: '#fff', fontWeight: 500 }}>
                  ✓ Remaining: {remainingRemoteDays} day(s)
                </Typography.Text>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Divider style={{ margin: "24px 0" }} />

      <Spin spinning={submitting}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            label="Remote Work Date Range"
            name="dateRange"
            rules={[
              {
                required: true,
                message: "Please select both start and end dates.",
              },
              {
                validator: (_, value) => {
                  if (!value || !value[0] || !value[1]) return Promise.resolve();
                  const days = calculateDays(value[0], value[1]);
                  if (days <= 0) {
                    return Promise.reject(new Error("End date must be after start date."));
                  }
                  if (days > MAX_REMOTE_DAYS) {
                    return Promise.reject(
                      new Error(`Remote work request cannot exceed ${MAX_REMOTE_DAYS} days.`)
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              placeholder={["Start date", "End date"]}
              format="DD MMM YYYY"
              locale={enGB.DatePicker}
              onChange={handleDateRangeChange}
              suffixIcon={<CalendarOutlined />}
              disabled={submitting}
            />
          </Form.Item>

          {selectedDays > 0 && (
            <Alert
              message={`${selectedDays} day(s) selected`}
              type={
                selectedDays > MAX_REMOTE_DAYS
                  ? "error"
                  : !isEdit && remainingRemoteDays - selectedDays < 0
                  ? "warning"
                  : selectedDays > CONSECUTIVE_DAYS_WARNING
                  ? "info"
                  : "info"
              }
              showIcon
              style={{ marginBottom: 16 }}
              description={
                selectedDays > MAX_REMOTE_DAYS
                  ? `Maximum ${MAX_REMOTE_DAYS} days allowed per request.`
                  : !isEdit && remainingRemoteDays - selectedDays < 0
                  ? "This will result in a negative balance. The deficit will be deducted from your future remote work balance."
                  : selectedDays > CONSECUTIVE_DAYS_WARNING
                  ? "Requesting more than 5 consecutive remote days may require special approval."
                  : null
              }
            />
          )}

          <Form.Item
            label="Reason / Description"
            name="reason"
            normalize={(v) => (v && typeof v === "string" ? v.trim() : v)}
            rules={[
              { required: true, message: "Please provide a reason." },
              {
                min: MIN_REASON_LENGTH,
                message: `Reason must be at least ${MIN_REASON_LENGTH} characters long.`,
              },
              {
                max: MAX_REASON_LENGTH,
                message: `Reason cannot exceed ${MAX_REASON_LENGTH} characters.`,
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Provide details about your remote work request..."
              showCount
              maxLength={MAX_REASON_LENGTH}
              className="apply-for-remote-request-modal__textarea"
              style={{ resize: "none" }}
              disabled={submitting}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button onClick={handleModalClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={submitting}
              >
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                icon={<HomeOutlined />}
              >
                {isEdit ? "Update Remote Work Request" : "Apply for Remote Work"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ApplyForRemoteRequestModal;